/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { useEffect, useRef } from 'react';
import { NativeEventEmitter } from 'react-native';
import NativeScreenshotDetector from '../native/NativeScreenshotDetector';
import type { ScreenshotTakenPayload } from '../native/NativeScreenshotDetector';

const EVENT_NAME = 'ScreenshotDetector:screenshotTaken';

/**
 * Listens for "screenshot taken" events from the native ScreenshotDetector TurboModule.
 * When the user takes a screenshot, the callback is invoked with payload (e.g. timestamp).
 *
 * Prefers the TurboModule callback API (addScreenshotTakenListener / removeListener).
 * Falls back to NativeEventEmitter for legacy bridge implementations that only emit events.
 *
 * No-op on web or when ScreenshotDetector is not registered by the host app.
 */
export function useScreenshotTakenListener(
  callback: (payload: ScreenshotTakenPayload) => void,
): void {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    const module = NativeScreenshotDetector;
    if (!module) return;

    let active = true;
    const listenerIdRef = { current: null as string | null };

    const runCallback = (payload: ScreenshotTakenPayload) => {
      callbackRef.current(payload);
    };

    // Prefer TurboModule callback API: register callback and clean up with removeListener(id).
    const hasAddListener =
      typeof (module as any).addScreenshotTakenListener === 'function';
    const hasRemoveListener =
      typeof (module as any).removeListener === 'function';

    const registerOnce = () => {
      if (!active || !hasAddListener) return;
      const result = (module as any).addScreenshotTakenListener(
        (payload: ScreenshotTakenPayload) => {
          runCallback(payload);
          // Android uses one-shot callbacks; re-register for the next screenshot.
          const id = listenerIdRef.current;
          if (id != null && hasRemoveListener) {
            (module as any).removeListener(id);
            listenerIdRef.current = null;
          }
          registerOnce();
        },
      ) as string | Promise<string> | undefined;
      if (typeof result === 'string') {
        listenerIdRef.current = result;
      } else if (
        result != null &&
        typeof (result as Promise<string>).then === 'function'
      ) {
        (result as Promise<string>).then((id: string) => {
          if (active) listenerIdRef.current = id;
        });
      }
    };

    if (hasAddListener) {
      registerOnce();
    }

    // Fallback: event emitter (legacy bridge that only emits events).
    const subscription = !hasAddListener
      ? new NativeEventEmitter(module as any).addListener(
          EVENT_NAME,
          runCallback,
        )
      : null;

    return () => {
      active = false;
      subscription?.remove();
      const id = listenerIdRef.current;
      if (
        id != null &&
        hasRemoveListener &&
        typeof (module as any).removeListener === 'function'
      ) {
        (module as any).removeListener(id);
      }
    };
  }, []);
}
